'use client'

import { useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { DropdownMenu, Icon, IconComponent, type MenuItem } from '@graftcode/design-system'

import { formatQuickStartLanguageLabel } from '@common/utils/formatQuickStartLanguageLabel'

import { documentationNavUrlPathForSection, resolveDocumentationSlug } from '@/lib/github/documentationSlug'
import { useDocumentationLanguageContext } from '@/shared/components/DocumentationLayoutShell/context/documentationLanguageContext'
import { getQuickStartLanguageIcon } from '@/shared/components/QuickStartLanguageTabs/utils/getQuickStartLanguageIcon'
import { buildDocumentationHref } from '@/shared/docs-shared.utils'
import { DOCUMENTATION_ARTICLE_SELECTORS_TEST_IDS } from '@/shared/docs-test-ids.const'

import styles from './DocumentationArticleSelectors.module.scss'

export function DocumentationArticleSelectors() {
  const { sections, selectedLanguage, setSelectedLanguage, routePrefix } = useDocumentationLanguageContext()
  const pathname = usePathname()
  const router = useRouter()

  const currentSection = useMemo(() => {
    if (!pathname) return null
    const prefix = routePrefix ? `${routePrefix}/` : '/'
    const slugPart = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname.replace(/^\//, '')
    if (!slugPart) return null
    const slugParts = slugPart.split('/').filter(Boolean)
    const resolved = resolveDocumentationSlug(slugParts, sections)
    return sections.find((s) => s.path === resolved?.articlePath) ?? null
  }, [pathname, routePrefix, sections])

  const displayLanguages = useMemo(() => {
    const langs = currentSection?.languages ?? []
    const real = langs.filter((l) => l !== 'default')
    return real.length > 0 ? real : langs.length > 0 ? langs : ['default']
  }, [currentSection])

  const handleLanguageClick = (lang: string) => {
    setSelectedLanguage(lang)
    if (currentSection) {
      router.push(buildDocumentationHref(documentationNavUrlPathForSection(currentSection, lang)), { scroll: false })
    }
  }

  const languageItems = useMemo<MenuItem[]>(
    () =>
      displayLanguages.map((lang) => ({
        type: 'item',
        id: lang,
        label: formatQuickStartLanguageLabel(lang),
        icon: getQuickStartLanguageIcon(lang),
        iconSize: 20,
        active: true,
        groups: [],
        selected: lang === selectedLanguage,
      })),
    [displayLanguages, selectedLanguage]
  )

  const activeLang =
    selectedLanguage && displayLanguages.includes(selectedLanguage)
      ? selectedLanguage
      : (displayLanguages[0] ?? 'default')

  const hasTechnologyChoice = displayLanguages.some((l) => l !== 'default')

  if (!hasTechnologyChoice) return null

  return (
    <div data-testid={DOCUMENTATION_ARTICLE_SELECTORS_TEST_IDS.root} className={styles.wrap}>
      <div className={styles.selectorGroup}>
        <span className={styles.label}>Your stack:</span>
        <DropdownMenu
          width="s"
          align="start"
          side="bottom"
          sideOffset={8}
          items={languageItems}
          onItemClick={handleLanguageClick}
        >
          <button type="button" className={styles.trigger} aria-label="Select technology stack">
            <IconComponent
              icon={getQuickStartLanguageIcon(activeLang)}
              size={24}
              className={styles.triggerIcon}
              aria-hidden
            />
            <span className={styles.triggerText}>{formatQuickStartLanguageLabel(activeLang)}</span>
            <IconComponent icon={Icon.ChevronDownRegular} size={24} className={styles.chevron} aria-hidden />
          </button>
        </DropdownMenu>
      </div>
    </div>
  )
}
